import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { API, formatGBP } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Plus,
  Trash,
  PencilSimple,
  Sparkle,
  Target as TargetIcon,
  LockKey,
  CheckCircle,
  Circle,
  ArrowsClockwise,
  Briefcase,
  Lightbulb,
  Warning,
  ChartLineUp,
} from "@phosphor-icons/react";
import MoneyInput from "@/components/MoneyInput";
import AIDisclaimer from "@/components/AIDisclaimer";
import AIInsightFootnote from "@/components/AIInsightFootnote";

const KINDS = [
  { k: "revenue", label: "Revenue £", help: "Hit a £ target" },
  { k: "clients", label: "Clients", help: "Win a specific number of clients" },
  { k: "cash", label: "Cash buffer £", help: "Build to a cash balance" },
  { k: "custom", label: "Custom", help: "Anything else (subscribers, hours saved…)" },
  {
    k: "big_decision",
    label: "Big decision",
    help: "Hire? Rent? Expand? Weigh it against your numbers.",
  },
];

const RECOMMENDED_QUARTERLY_CAP = 3;

// Storage key shared with WhatIfSlider for the "show me the impact" jump.
const WHATIF_HANDOFF_KEY = "forecastly_whatif_handoff_v1";

// Lightweight keyword heuristic to soft-suggest the Big Decision kind when the
// user is clearly weighing a hire/rent/expansion call. Zero LLM cost. The user
// can always override from the picker — this is a nudge, not a lock-in.
const BIG_DECISION_PATTERNS = /\b(hire|hiring|recruit|employ|rent|renting|lease|leasing|office|studio|warehouse|expand|expansion|scale\s+up|open\s+a|premises|workspace|equipment|car|van|vehicle|invest|buy\s+(a|new)|should\s+i|can\s+i\s+afford|moving|relocate)\b/i;
function looksLikeBigDecision(...texts) {
  return texts.filter(Boolean).some((t) => BIG_DECISION_PATTERNS.test(String(t)));
}

// Display sort: Big Decisions first (most strategic), then standard goals by
// closest deadline, with deadline-less goals last.
function sortGoals(goals) {
  const score = (g) => {
    if (g.kind === "big_decision") return -1; // top
    const m = g.target_month_index;
    if (m === null || m === undefined) return 9999; // no deadline → bottom
    return Number(m);
  };
  return [...goals].sort((a, b) => {
    const sa = score(a);
    const sb = score(b);
    if (sa !== sb) return sa - sb;
    // Stable tie-break: newer goals first
    return (b.created_at || "").localeCompare(a.created_at || "");
  });
}

/**
 * Goals page — v2.
 *
 * Captures the user's "why" so AI insights can be personal. Generates a tickable
 * checklist of next moves for standard goals, OR a Big-Decision framing
 * (affordability + before-yes + after-watchpoints) for kind="big_decision".
 * Honest about plan gating: one-off users can set 1 goal as a taster.
 */
export default function GoalsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const plan = user?.plan;
  const isOneOff = plan === "one_off" || plan === "none";
  const isCancelled = plan === "cancelled";
  const aiLocked = isOneOff || isCancelled;

  const [goals, setGoals] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [busy, setBusy] = useState(false);
  const [aiBusyGoalId, setAiBusyGoalId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  // Iter38 — when set, the form saves via PATCH instead of POST. All fields
  // (title, why, kind, target, deadline, metric) are editable inline in the
  // same card the "Add goal" button opens, replacing the old two-`window.prompt`
  // hack that only let users tweak title + why.
  const [editingId, setEditingId] = useState(null);
  // Track whether the user has explicitly picked a kind — if they haven't, we
  // auto-apply the keyword heuristic. Once they pick from the dropdown we stop
  // overriding.
  const [kindTouched, setKindTouched] = useState(false);
  const [form, setForm] = useState({
    title: "",
    why: "",
    target_value: "",
    target_month_index: "",
    kind: "revenue",
    metric: "",
  });

  const load = async () => {
    const [g, f] = await Promise.all([axios.get(`${API}/goals`), axios.get(`${API}/forecast`)]);
    setGoals(g.data || []);
    setForecast(f.data);
  };

  useEffect(() => {
    load();
  }, []);

  // Apply the keyword heuristic only while the user hasn't manually picked a
  // kind. Suggests big_decision when the title/why hints at hiring, renting,
  // expansion, etc. Suggests nothing back (i.e. doesn't override) when the
  // heuristic finds nothing — we leave their last pick alone.
  useEffect(() => {
    if (kindTouched) return;
    if (!showForm) return;
    const looksBig = looksLikeBigDecision(form.title, form.why);
    if (looksBig && form.kind !== "big_decision") {
      setForm((f) => ({ ...f, kind: "big_decision" }));
    } else if (!looksBig && form.kind === "big_decision") {
      // Roll back if the user clears the trigger words AND they haven't touched
      // the picker — prevents the kind getting stuck on big_decision after edits.
      setForm((f) => ({ ...f, kind: "revenue" }));
    }
  }, [form.title, form.why, kindTouched, showForm]);  // eslint-disable-line react-hooks/exhaustive-deps

  const monthOptions = (forecast?.months || []).map((m, i) => ({ idx: i, label: m.label }));
  const atOneOffLimit = isOneOff && goals.length >= 1;
  const overQuarterlyRecommendation = useMemo(
    () => !aiLocked && goals.length >= RECOMMENDED_QUARTERLY_CAP,
    [goals.length, aiLocked],
  );
  const sortedGoals = useMemo(() => sortGoals(goals), [goals]);

  const resetForm = () => {
    setForm({ title: "", why: "", target_value: "", target_month_index: "", kind: "revenue", metric: "" });
    setKindTouched(false);
    setEditingId(null);
    setShowForm(false);
  };

  const startEditGoal = (goal) => {
    // Populate the same "Add" card with the goal's existing values so every
    // field is editable — title, why, kind, target, deadline, metric.
    setForm({
      title: goal.title || "",
      why: goal.why || "",
      target_value: goal.target_value != null ? String(goal.target_value) : "",
      target_month_index: goal.target_month_index != null ? String(goal.target_month_index) : "",
      kind: goal.kind || "revenue",
      metric: goal.metric || "",
    });
    setKindTouched(true);  // don't let the heuristic flip their kind after they've edited
    setEditingId(goal.id);
    setShowForm(true);
    // Scroll the form into view so it's obvious where the edit is happening.
    setTimeout(() => {
      const el = document.querySelector('[data-testid="goal-form"]');
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Give your goal a name first.");
      return;
    }
    if (!form.why.trim()) {
      toast.error("Tell us what's behind this goal — that's how the AI gets personal.");
      return;
    }
    setBusy(true);
    const payload = {
      title: form.title.trim(),
      why: form.why.trim(),
      target_value: form.target_value !== "" && form.target_value != null ? Number(form.target_value) : null,
      target_month_index: form.target_month_index !== "" ? Number(form.target_month_index) : null,
      kind: form.kind,
      metric: form.kind === "custom" ? form.metric || null : null,
    };
    try {
      if (editingId) {
        const res = await axios.patch(`${API}/goals/${editingId}`, payload);
        setGoals((arr) => arr.map((x) => (x.id === res.data.id ? res.data : x)));
        toast.success("Goal updated.");
      } else {
        await axios.post(`${API}/goals`, payload);
        toast.success("Goal set. Let's make it happen.");
      }
      resetForm();
      await load();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "Could not save goal";
      if (status === 402) toast.message(detail);
      else toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this goal and its action plan? This can't be undone.")) return;
    await axios.delete(`${API}/goals/${id}`);
    toast.success("Goal removed.");
    await load();
  };

  const generateActions = async (g) => {
    setAiBusyGoalId(g.id);
    try {
      const res = await axios.post(`${API}/ai/goal-actions`, { goal_id: g.id });
      setGoals((arr) => arr.map((x) => (x.id === res.data.id ? res.data : x)));
      toast.success(
        g.kind === "big_decision"
          ? "Decision framework ready."
          : "Action plan ready. Tick them off as you go.",
      );
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "Could not generate actions.";
      if (status === 429 || status === 402) toast.message(detail);
      else toast.error(detail);
    } finally {
      setAiBusyGoalId(null);
    }
  };

  const toggleAction = async (goalId, actionId, completed) => {
    try {
      const res = await axios.patch(`${API}/goals/${goalId}/actions/${actionId}`, { completed });
      setGoals((arr) => arr.map((x) => (x.id === res.data.id ? res.data : x)));
    } catch {
      toast.error("Could not update — try again.");
    }
  };

  // Big-Decision lifecycle: when every pre-commitment step is ticked, the user
  // can promote this into a standard execution-stage goal. Fresh AI checklist
  // for the post-commitment work, original "why" carried forward.
  const promoteDecision = async (g) => {
    if (!window.confirm(
      `You've worked through every pre-commitment step. Promote "${g.title}" into an execution-stage goal? We'll generate a fresh checklist focused on making this decision land well.`,
    )) {
      return;
    }
    setAiBusyGoalId(g.id);
    try {
      const res = await axios.post(`${API}/goals/${g.id}/promote-to-standard`);
      setGoals((arr) => arr.map((x) => (x.id === res.data.id ? res.data : x)));
      toast.success("You're committed — here's your execution checklist.");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "Could not promote the goal.";
      if (status === 402 || status === 429) toast.message(detail);
      else toast.error(detail);
    } finally {
      setAiBusyGoalId(null);
    }
  };

  // Big-Decision → Dashboard handoff. We stash a tiny payload in localStorage
  // and bounce to /dashboard. The WhatIfSlider reads it on mount and pre-loads
  // the cost lever to show the impact of taking on this decision.
  const showImpact = (g) => {
    if (g.kind !== "big_decision") return;
    if (!g.target_value || g.target_value <= 0) {
      toast.message(
        "Add a target value (e.g. £900/month) on this decision to see its impact on your numbers.",
      );
      return;
    }
    try {
      window.localStorage.setItem(
        WHATIF_HANDOFF_KEY,
        JSON.stringify({
          goal_id: g.id,
          goal_title: g.title,
          monthly_cost_adjustment: Number(g.target_value),
          created_at: new Date().toISOString(),
        }),
      );
    } catch {
      /* localStorage disabled — slider just won't pre-load */
    }
    navigate("/dashboard?modelling=1");
  };

  return (
    <div className="space-y-10">
      <div>
        <div className="section-eyebrow">Goals</div>
        <h1 className="font-heading uppercase text-4xl md:text-5xl tracking-wide mt-4 leading-[1.05]">
          What are you working towards in the{" "}
          <span className="highlight-yellow inline-block">next 12 months?</span>
        </h1>
        <p className="mt-3 text-black/70 leading-relaxed">
          Set a couple of meaningful goals — small enough to move on, big enough to matter.
          forecastly then generates a tailored action plan for each one, taking your numbers,
          your rhythm and what you've already done into account.
        </p>
      </div>

      {/* Quarterly recommendation banner */}
      {overQuarterlyRecommendation && (
        <div
          className="card bg-[#FFF7E5] flex items-start gap-3"
          data-testid="goals-too-many-banner"
        >
          <Lightbulb size={22} weight="duotone" className="shrink-0 mt-0.5" />
          <div className="text-sm leading-relaxed">
            <strong>Gentle nudge:</strong> we recommend focusing on{" "}
            <strong>2-3 goals per quarter</strong>. That gives you the runway to actually act on
            them and see real movement. You can always add more later once these are in flight.
          </div>
        </div>
      )}

      {/* One-off taster banner */}
      {atOneOffLimit && (
        <div className="card bg-[#FFF7E5] flex items-start gap-3" data-testid="goals-one-off-banner">
          <LockKey size={20} weight="bold" className="shrink-0 mt-0.5" />
          <div className="text-sm leading-relaxed flex-1">
            You've used your one taster goal. Goals are most powerful when the AI weighs in on
            them month after month — taking your latest actuals into account and giving you a
            tailored set of next moves.{" "}
            <Link to="/upgrade" className="underline font-semibold">
              Unlock unlimited goals + AI action plans
            </Link>
            .
          </div>
        </div>
      )}

      {/* Add-goal CTA + form */}
      {!showForm ? (
        <div className="flex flex-wrap gap-3 items-center">
          <button
            type="button"
            className="btn-primary"
            onClick={() => setShowForm(true)}
            disabled={atOneOffLimit}
            data-testid="goals-show-form-btn"
          >
            <Plus size={18} weight="bold" /> Add a goal
          </button>
          {!atOneOffLimit && (
            <span className="text-xs text-black/55">
              {goals.length === 0
                ? "Start with one clear thing you want this year."
                : `${goals.length} active goal${goals.length === 1 ? "" : "s"}.`}
            </span>
          )}
        </div>
      ) : (
        <form className="card space-y-5" onSubmit={submit} data-testid="goal-form">
          <div className="flex items-start gap-3">
            <Sparkle size={22} weight="duotone" className="mt-1 shrink-0" />
            <div className="text-sm leading-relaxed text-black/75">
              <strong>A tip from Magdalena:</strong> we recommend setting{" "}
              <strong>2-3 goals per quarter</strong> — enough to keep momentum, few enough to
              actually action. Quality over quantity. You can always come back next quarter.
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1">Your goal *</label>
            <input
              className="input"
              data-testid="goal-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Hire a part-time VA by September"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1">
              What's behind this goal? *
            </label>
            <textarea
              className="input min-h-[88px] py-2"
              data-testid="goal-why"
              value={form.why}
              onChange={(e) => setForm({ ...form, why: e.target.value })}
              placeholder="e.g. I want to stop working evenings and reclaim 8 hours a week for sales. This is the year I stop being the bottleneck."
            />
            <p className="text-xs text-black/55 mt-1">
              This single sentence is the magic — it personalises every action the AI suggests.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold mb-1">Type</label>
              <select
                className="input"
                data-testid="goal-kind"
                value={form.kind}
                onChange={(e) => {
                  setKindTouched(true);
                  setForm({ ...form, kind: e.target.value });
                }}
              >
                {KINDS.map((k) => (
                  <option key={k.k} value={k.k}>
                    {k.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-black/55 mt-1">
                {KINDS.find((x) => x.k === form.kind)?.help}
              </p>
              {/* Soft suggestion when the keyword heuristic flips the kind for them */}
              {!kindTouched && form.kind === "big_decision" && (form.title || form.why) && (
                <p
                  className="text-xs text-black/85 mt-1.5 inline-flex items-center gap-1"
                  data-testid="goal-kind-suggestion"
                >
                  <Sparkle size={12} weight="fill" /> This sounds like a{" "}
                  <strong>Big Decision</strong> — we've pre-selected it for you. Change it any time.
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">
                Target value{" "}
                <span className="text-black/45 font-normal">
                  (optional{form.kind === "big_decision" ? ", e.g. monthly cost" : ""})
                </span>
              </label>
              <MoneyInput
                value={form.target_value}
                onChange={(n) => setForm({ ...form, target_value: n })}
                testid="goal-value"
                prefix={
                  form.kind === "revenue" || form.kind === "cash" || form.kind === "big_decision"
                    ? "£"
                    : null
                }
                placeholder={form.kind === "big_decision" ? "e.g. 900 (rent £/mo)" : "e.g. 10"}
              />
            </div>
          </div>

          {form.kind !== "big_decision" && (
            <div>
              <label className="block text-xs font-semibold mb-1">
                By month <span className="text-black/45 font-normal">(optional)</span>
              </label>
              <select
                className="input"
                data-testid="goal-month"
                value={form.target_month_index}
                onChange={(e) => setForm({ ...form, target_month_index: e.target.value })}
              >
                <option value="">No deadline</option>
                {monthOptions.map((m) => (
                  <option key={m.idx} value={m.idx}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {form.kind === "custom" && (
            <div data-testid="goal-metric-row">
              <label className="block text-xs font-semibold mb-1">What are you tracking?</label>
              <input
                className="input"
                data-testid="goal-metric"
                value={form.metric}
                onChange={(e) => setForm({ ...form, metric: e.target.value })}
                placeholder="e.g. newsletter subscribers, hours saved per week…"
              />
            </div>
          )}

          <div className="flex flex-wrap gap-3 justify-end">
            <button
              type="button"
              className="btn-secondary"
              onClick={resetForm}
              data-testid="goal-cancel-btn"
            >
              Cancel
            </button>
            <button className="btn-primary" disabled={busy} data-testid="goal-add-btn">
              <Plus size={18} weight="bold" />{" "}
              {busy
                ? "Saving…"
                : editingId
                  ? "Save changes"
                  : "Add this goal"}
            </button>
          </div>
        </form>
      )}

      {/* Goals list */}
      <div className="space-y-4">
        {goals.length === 0 && !showForm && (
          <div className="card text-black/70" data-testid="goals-empty">
            No goals yet. Start with one clear thing you want this year.
          </div>
        )}
        {sortedGoals.map((g) => (
          <GoalCard
            key={g.id}
            goal={g}
            forecast={forecast}
            aiLocked={aiLocked}
            isCancelled={isCancelled}
            busy={aiBusyGoalId === g.id}
            onGenerate={() => generateActions(g)}
            onToggleAction={(actionId, completed) => toggleAction(g.id, actionId, completed)}
            onDelete={() => remove(g.id)}
            onEdit={startEditGoal}
            onShowImpact={() => showImpact(g)}
            onPromote={() => promoteDecision(g)}
          />
        ))}
      </div>
    </div>
  );
}

/** Single goal card — branches on kind === 'big_decision' for the alternative layout. */
function GoalCard({ goal, forecast, aiLocked, isCancelled, busy, onGenerate, onToggleAction, onDelete, onEdit, onShowImpact, onPromote }) {
  const isDecision = goal.kind === "big_decision";
  const actions = goal.actions || [];
  const completedCount = actions.filter((a) => a.completed).length;
  const totalCount = actions.length;
  const allDone = totalCount > 0 && completedCount === totalCount;
  const hasOutput = isDecision ? !!goal.decision?.affordability_snapshot : actions.length > 0;
  const generatedAt = goal.actions_generated_at;
  const canModelImpact = isDecision && Number(goal.target_value) > 0;
  const isPromoted = !!goal.promoted_from_big_decision;

  return (
    <div className="card" data-testid={`goal-${goal.id}`}>
      {/* Iter41 mobile fix — action buttons (Regenerate/Edit/Delete) used to sit
          on the SAME row as the goal title on phones, cropping the title with
          the button chrome. We now stack them on <sm, keep inline on ≥sm. */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-start justify-between gap-4">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <div className="section-number shrink-0">
            {isDecision ? <Briefcase size={20} weight="bold" /> : <TargetIcon size={20} weight="bold" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-heading uppercase text-xl">{goal.title}</div>
              {isDecision && (
                <span
                  className="text-[10px] font-bold uppercase tracking-wider bg-black text-white rounded-full px-2 py-0.5"
                  data-testid={`goal-kind-chip-${goal.id}`}
                >
                  Big decision
                </span>
              )}
              {!isDecision && isPromoted && (
                <span
                  className="text-[10px] font-bold uppercase tracking-wider bg-[#FFBD4A] text-black rounded-full px-2 py-0.5"
                  data-testid={`goal-promoted-chip-${goal.id}`}
                  title="Promoted from a big decision after every pre-yes step was ticked"
                >
                  Execution stage
                </span>
              )}
              {!isDecision && totalCount > 0 && (
                <span
                  className="text-xs font-semibold text-black/65"
                  data-testid={`goal-progress-${goal.id}`}
                >
                  {completedCount}/{totalCount} actions done
                </span>
              )}
            </div>
            <div className="text-sm text-black/60 mt-1">
              {goal.kind === "revenue" && goal.target_value ? `Target: ${formatGBP(goal.target_value)}` : null}
              {goal.kind === "cash" && goal.target_value ? `Buffer target: ${formatGBP(goal.target_value)}` : null}
              {goal.kind === "clients" && goal.target_value ? `Target: ${goal.target_value} clients` : null}
              {goal.kind === "custom" && goal.target_value
                ? `Target: ${goal.target_value}${goal.metric ? ` ${goal.metric}` : ""}`
                : null}
              {goal.kind === "big_decision" && goal.target_value
                ? `Cost involved: ${formatGBP(goal.target_value)}`
                : null}
              {goal.target_month_index !== null && goal.target_month_index !== undefined
                ? ` · by ${forecast?.months?.[goal.target_month_index]?.label || `month ${goal.target_month_index + 1}`}`
                : null}
            </div>
            {goal.why && (
              <div
                className="mt-3 text-sm text-black/75 italic leading-relaxed border-l-[3px] border-[#FFBD4A] pl-3"
                data-testid={`goal-why-${goal.id}`}
              >
                "{goal.why}"
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2 shrink-0 flex-wrap sm:flex-nowrap">
          {!aiLocked && (
            <button
              className="btn-secondary"
              onClick={onGenerate}
              disabled={busy}
              data-testid={`goal-generate-btn-${goal.id}`}
              title={
                hasOutput
                  ? "Regenerate (allowed when all steps done, 4 weeks elapsed, this month's actuals logged, or a new weekly check-in logged)"
                  : "Generate your AI action plan"
              }
            >
              {hasOutput ? (
                <ArrowsClockwise size={16} weight="bold" className={busy ? "animate-spin" : ""} />
              ) : (
                <Sparkle size={16} weight="fill" />
              )}
              {busy
                ? "Thinking…"
                : hasOutput
                  ? "Regenerate"
                  : isDecision
                    ? "Run the numbers on this"
                    : "Get my action plan"}
            </button>
          )}
          <button
            className="btn-secondary !px-3 !py-2"
            onClick={() => onEdit(goal)}
            data-testid={`goal-edit-${goal.id}`}
            title="Edit this goal"
          >
            <PencilSimple size={16} />
          </button>
          <button
            className="btn-secondary !px-3 !py-2"
            onClick={onDelete}
            data-testid={`goal-delete-${goal.id}`}
            title="Delete goal"
          >
            <Trash size={16} />
          </button>
        </div>
      </div>

      {/* Output area */}
      {aiLocked && (
        <div
          className="mt-5 text-sm text-black/65 leading-relaxed flex items-start gap-2"
          data-testid={`goal-paywall-${goal.id}`}
        >
          <LockKey size={16} weight="bold" className="mt-0.5 flex-shrink-0" />
          <span>
            {isCancelled
              ? "AI action plans are part of the monthly subscription. "
              : "AI action plans turn this goal into a tickable checklist, tailored to your numbers month after month. "}
            <Link to="/upgrade" className="underline font-semibold">
              {isCancelled ? "Reactivate your subscription" : "Upgrade to subscription"}
            </Link>
            .
          </span>
        </div>
      )}

      {!aiLocked && isDecision && goal.decision?.affordability_snapshot && (
        <div className="mt-5 space-y-4" data-testid={`goal-decision-${goal.id}`}>
          <div className="rounded-2xl bg-[#FFF7E5] border-[1.5px] border-black p-4">
            <div className="text-xs uppercase tracking-wider font-semibold flex items-center gap-1.5 text-black/70">
              <Warning size={14} weight="fill" /> Affordability snapshot
            </div>
            <p className="mt-2 leading-relaxed text-black/85">
              {goal.decision.affordability_snapshot}
            </p>
            {canModelImpact && (
              <p className="mt-3 text-sm text-black/70 leading-relaxed">
                Tap the button below and we'll model this decision straight into the What-If
                stress-tester on your dashboard — so you can see the impact on your cash balance
                across the next 12 months before you commit.
              </p>
            )}
              <div className="flex flex-wrap gap-2 mt-4">
                {canModelImpact && (
                  <button
                    type="button"
                    onClick={onShowImpact}
                    className="btn-primary"
                    data-testid={`goal-show-impact-${goal.id}`}
                  >
                    <ChartLineUp size={16} weight="bold" /> Yes, show me the impact
                  </button>
                )}
                {allDone && (
                  <button
                    type="button"
                    onClick={onPromote}
                    disabled={busy}
                    className="btn-primary"
                    data-testid={`goal-promote-${goal.id}`}
                  >
                    <CheckCircle size={16} weight="fill" />
                    {busy ? "Generating execution plan…" : "I'm going ahead — get my execution plan"}
                  </button>
                )}
              </div>
            </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-2xl border-[1.5px] border-black p-4">
              <div className="flex items-center justify-between mb-2 gap-2">
                <div className="text-xs uppercase tracking-wider font-semibold text-black/70">
                  Do BEFORE saying yes
                </div>
                {totalCount > 0 && (
                  <span
                    className="text-xs font-semibold text-black/65"
                    data-testid={`goal-decision-progress-${goal.id}`}
                  >
                    {completedCount}/{totalCount} done
                  </span>
                )}
              </div>
              {actions.length > 0 ? (
                <ul className="space-y-2">
                  {actions.map((a) => (
                    <li key={a.id} className="flex items-start gap-2.5">
                      <button
                        type="button"
                        className="mt-0.5 shrink-0"
                        onClick={() => onToggleAction(a.id, !a.completed)}
                        data-testid={`goal-action-toggle-${goal.id}-${a.id}`}
                        aria-label={a.completed ? "Mark incomplete" : "Mark complete"}
                      >
                        {a.completed ? (
                          <CheckCircle size={18} weight="fill" className="text-[#1B7E3C]" />
                        ) : (
                          <Circle size={18} weight="regular" />
                        )}
                      </button>
                      <span
                        className={`text-sm leading-relaxed ${
                          a.completed ? "line-through text-black/50" : "text-black/85"
                        }`}
                      >
                        {a.text}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                // Fallback for legacy decisions without actions[]
                <ol className="list-decimal list-inside space-y-1.5 text-sm leading-relaxed">
                  {(goal.decision.before_yes || []).map((s, i) => (
                    <li key={i} data-testid={`goal-before-${goal.id}-${i}`}>
                      {s}
                    </li>
                  ))}
                </ol>
              )}
            </div>
            <div className="rounded-2xl border-[1.5px] border-black p-4">
              <div className="text-xs uppercase tracking-wider font-semibold text-black/70 mb-2">
                Watch AFTER saying yes
              </div>
              <ol className="list-decimal list-inside space-y-1.5 text-sm leading-relaxed">
                {(goal.decision.after_watchpoints || []).map((s, i) => (
                  <li key={i} data-testid={`goal-after-${goal.id}-${i}`}>
                    {s}
                  </li>
                ))}
              </ol>
            </div>
          </div>
          {generatedAt && (
            <AIInsightFootnote
              generatedAt={generatedAt}
              showChecklist
              allDone={allDone}
              totalCount={totalCount}
              className="mt-4"
            />
          )}
        </div>
      )}

      {!aiLocked && !isDecision && actions.length > 0 && (
        <div className="mt-5" data-testid={`goal-actions-${goal.id}`}>
          {goal.actions_insight && (
            <div
              className="rounded-2xl bg-[#FFF7E5] border-[1.5px] border-black p-4 mb-4"
              data-testid={`goal-actions-insight-${goal.id}`}
            >
              <div className="text-xs uppercase tracking-wider font-semibold text-black/70 mb-2">
                Reading your numbers
              </div>
              <p className="leading-relaxed text-black/85 text-sm">{goal.actions_insight}</p>
            </div>
          )}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs uppercase tracking-wider font-semibold text-black/70">
              Your action plan
            </div>
            {allDone && (
              <span
                className="text-xs font-semibold inline-flex items-center gap-1 text-[#1B7E3C]"
                data-testid={`goal-all-done-${goal.id}`}
              >
                <CheckCircle size={14} weight="fill" /> All done — regenerate the next set
              </span>
            )}
          </div>
          <ul className="mt-2 space-y-2">
            {actions.map((a) => (
              <li key={a.id} className="flex items-start gap-3">
                <button
                  type="button"
                  className="mt-0.5 shrink-0"
                  onClick={() => onToggleAction(a.id, !a.completed)}
                  data-testid={`goal-action-toggle-${goal.id}-${a.id}`}
                  aria-label={a.completed ? "Mark incomplete" : "Mark complete"}
                >
                  {a.completed ? (
                    <CheckCircle size={20} weight="fill" className="text-[#1B7E3C]" />
                  ) : (
                    <Circle size={20} weight="regular" />
                  )}
                </button>
                <span
                  className={`text-sm leading-relaxed ${
                    a.completed ? "line-through text-black/50" : "text-black/85"
                  }`}
                >
                  {a.text}
                </span>
              </li>
            ))}
          </ul>
          {generatedAt && (
            <AIInsightFootnote
              generatedAt={generatedAt}
              showChecklist
              allDone={actions.length > 0 && actions.every((a) => a.completed)}
              totalCount={actions.length}
              className="mt-4"
            />
          )}
        </div>
      )}

      {!aiLocked && !hasOutput && (
        <p className="mt-5 text-sm text-black/55" data-testid={`goal-empty-${goal.id}`}>
          {isDecision
            ? "Tap 'Run the numbers on this' and we'll show you an affordability snapshot, what to line up before saying yes, and what to watch after."
            : "Tap 'Get my action plan' and we'll turn this into a tickable checklist tailored to your numbers."}
        </p>
      )}
    </div>
  );
}
